// ── <ExplorerRankBadge /> — persistent top-right rank chip (Phase 6).
// Shows the Explorer Rank TIER for the current focus subject. No XP.
// Opens a popover explaining the current tier and exactly what moves it.
import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { computeSubjectRank } from "@/lib/explorerRank";
import { getFocusSubject } from "@/lib/timedRuns";
import { trackEvent } from "@/lib/analytics";

const LAST_TIER_KEY = "mwq_last_tier_id_v1";
const SUBJECT_LABEL = { maths: "Maths", english: "English", geography: "Geography", science: "Science" };

export default function ExplorerRankBadge() {
  const [subject, setSubject] = useState(() => getFocusSubject());
  const [snap, setSnap] = useState(() => computeSubjectRank(subject));
  const [pulse, setPulse] = useState(false);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    const refresh = () => {
      const subj = getFocusSubject();
      setSubject(subj);
      const s = computeSubjectRank(subj);
      setSnap(s);
      try {
        const prev = localStorage.getItem(LAST_TIER_KEY);
        if (prev && prev !== s.tier.id && !s.isNew) {
          setPulse(true);
          window.setTimeout(() => setPulse(false), 1600);
          try { trackEvent("rank_up", { to: s.tier.id, subject: subj }); } catch { /* ignore */ }
        }
        localStorage.setItem(LAST_TIER_KEY, s.tier.id);
      } catch { /* ignore */ }
    };
    refresh();
    for (const ev of ["focus", "mwq-timed-run-logged", "mwq-focus-subject-changed", "we-active-child-changed"]) {
      window.addEventListener(ev, refresh);
    }
    return () => {
      for (const ev of ["focus", "mwq-timed-run-logged", "mwq-focus-subject-changed", "we-active-child-changed"]) {
        window.removeEventListener(ev, refresh);
      }
    };
  }, []);

  const label = SUBJECT_LABEL[subject] || subject;

  return (
    <div className="relative" data-testid="explorer-rank-badge">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        aria-label={`${label} Explorer Rank: ${snap.tier.label}`}
        data-testid="explorer-rank-badge-btn"
        className={`inline-flex items-center gap-1 h-8 md:h-9 px-2 md:px-2.5 rounded-full border-2 border-slate-900 bg-white shadow-[0_2px_0_0_rgba(15,23,42,1)] hover:-translate-y-[1px] transition-transform ${pulse ? "ring-4 ring-yellow-400" : ""}`}
      >
        <span aria-hidden className="text-base leading-none">{snap.tier.emoji}</span>
        <span className="font-black text-[10px] md:text-xs text-slate-900 leading-none max-w-[92px] truncate">
          {snap.tier.label}
        </span>
      </button>
      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ opacity: 0, y: -6, scale: 0.98 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: -6, scale: 0.98 }}
            transition={{ duration: 0.15 }}
            role="dialog"
            data-testid="explorer-rank-popover"
            className="absolute right-0 mt-1.5 w-[min(280px,calc(100vw-24px))] rounded-2xl border-[3px] border-slate-900 bg-white p-3 shadow-[6px_6px_0_0_rgba(15,23,42,1)] z-[60]"
          >
            <p className="text-[10px] font-black uppercase tracking-widest text-slate-500">{label} · Explorer Rank</p>
            <p className="mt-0.5 font-heading text-lg font-black text-slate-900 leading-tight">
              {snap.tier.emoji} {snap.tier.label}
            </p>
            <p className="mt-1 text-xs font-semibold text-slate-700" data-testid="explorer-rank-popover-progress">
              {snap.progressLabel}
            </p>
            <Link
              to="/mastery-trial"
              onClick={() => setOpen(false)}
              className="mt-2.5 inline-flex items-center gap-1 h-9 px-3 rounded-full border-2 border-slate-900 bg-emerald-300 font-bold text-xs shadow-[0_2px_0_0_rgba(15,23,42,1)]"
            >
              Prove it →
            </Link>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}
